<template>
    <modal
        :active="active"
        @closed="$emit('closed', true)"
        :title="title()"
        :large="true"
    >
        <div class="columns is-tablet">
            <form-field
                class="column"
                label="Navn"
                help-text="Det officielle navn (eks.: NORDIC A/S)"
                :error="error.name"
            >
                <input-text v-model="form.name" :error="error.name" />
            </form-field>

            <form-field
                class="column"
                label="Land"
                :error="error.country"
                help-text="Landekoden (eks.: DK)"
            >
                <InputSelectSearch
                    :options="listCountries"
                    v-model="countryId"
                    :error="error.country"
                    label="title"
                />
            </form-field>
        </div>

        <div class="columns is-tablet">
            <form-field
                class="column"
                label="Hovednummer"
                :error="error.phonenumber"
                help-text="Landekode+nummer - <u>ingen</u> mellemrum (eks.: +4570222737)"
            >
                <input-text
                    v-model="form.phonenumber"
                    :error="error.phonenumber"
                />
            </form-field>

            <form-field
                class="column"
                label="Adresse"
                :error="error.address"
                help-text="Vejnavn + nummer (eks.: Industrivej 3C)"
            >
                <input-text v-model="form.address" :error="error.address" />
            </form-field>
        </div>

        <div class="columns is-tablet">
            <form-field
                class="column"
                label="Postnummer"
                :error="error.zipcode"
                help-text="OK med bogstaver som der er i Irland, men ingen mellemrum (eks.: 5500)"
            >
                <input-text v-model="form.zipcode" :error="error.zipcode" />
            </form-field>

            <form-field
                class="column"
                label="By"
                :error="error.city"
                help-text="Bynavn (eks.: Middelfart)"
            >
                <input-text v-model="form.city" :error="error.city" />
            </form-field>
        </div>

        <div class="columns is-tablet">
            <form-field
                class="column"
                label="E-mail"
                :error="error.email"
                help-text="Fakturamail (eks.: finance@nordic.nu)"
            >
                <input-text v-model="form.email" :error="error.email" />
            </form-field>

            <form-field
                class="column"
                label="KM (tur/retur)"
                help-text="KM opmålt via Google (kun aktuelt ved danske kunder!)"
                :error="error.kilometers"
            >
                <input-text
                    v-model="form.kilometers"
                    :error="error.kilometers"
                />
            </form-field>
        </div>

        <div class="columns is-tablet">
            <form-field
                class="column"
                label="CVR nummer"
                help-text="Landekode+CVR (eks. DK34603103)"
                :error="error.cvr_number"
            >
                <input-text
                    v-model="form.cvr_number"
                    :error="error.cvr_number"
                />
            </form-field>

            <form-field
                class="column"
                label="EAN nummer"
                help-text="EAN hvis kunden oplyser det (kun i DK, og primært kun offentlige institutioner)"
                :error="error.ean_number"
            >
                <input-text
                    v-model="form.ean_number"
                    :error="error.ean_number"
                />
            </form-field>
        </div>

        <div class="columns is-tablet">
            <form-field
                class="column"
                label="CC emails (komma separeret liste uden mellemrum)"
                help-text="Mailadresser kan indtastes her, så de får en kopi af fakturaen"
                :error="error.cc_emails"
            >
                <input-text
                    v-model="form.cc_emails"
                    :error="error.cc_emails"
                    @input="error.cc_emails = null"
                />
            </form-field>
            <div />
        </div>

        <div v-if="economicIsEnabled" class="columns is-tablet">
            <form-field
                class="column"
                label="Kundegruppe"
                help-text="Vælg om det er ”Danmark” eller ”Udland”"
                :error="error.customer_group"
                inner-container-class="select is-fullwidth"
            >
                <input-select
                    :options="economic.customerGroups"
                    v-model="form.customer_group"
                    :error="error.customer_group"
                />
            </form-field>

            <form-field
                class="column"
                label="Betalingsbetingelser"
                help-text="Vores standard er ”Netto 14 dage”, og hvis det er udland skal der i de fleste tilfælde vælges ”Netto 14 days”. <b>Ved tvivl kontakt FG.</b>"
                :error="error.payment_critea"
                inner-container-class="select is-fullwidth"
            >
                <input-select
                    :options="economic.paymentTerms"
                    v-model="form.payment_critea"
                    :error="error.payment_critea"
                />
            </form-field>
        </div>

        <div v-if="economicIsEnabled" class="columns is-tablet">
            <form-field
                class="column"
                label="Momszone"
                help-text="Vælg område (DK=Domestic, EU=EU, Abroad=Uden for EU & Domestic exempt VAT= DK u. moms)"
                :error="error.tax_zone"
                inner-container-class="select is-fullwidth"
            >
                <input-select
                    :options="economic.vatZones"
                    v-model="form.tax_zone"
                    :error="error.tax_zone"
                />
            </form-field>

            <form-field
                class="column"
                label="Valuta"
                help-text="Vælg DKK for DK, og EUR for øvrige lande"
                :error="error.currency"
                inner-container-class="select is-fullwidth"
            >
                <input-select
                    :options="currencies"
                    v-model="form.currency"
                    :error="error.currency"
                />
            </form-field>
        </div>

        <div v-if="economicIsEnabled && !isEdit" class="columns is-tablet">
            <form-field
                class="column"
                label="Kundenr."
                help-text="Lad feltet være tomt – Systemet generer selv et kundenr."
                :error="error.economic_id"
            >
                <input-text
                    v-model="form.economic_id"
                    :error="error.economic_id"
                />
            </form-field>
        </div>

        <div class="customer-details" :class="{ open: detailsOpen }">
            <h4 @click="detailsOpen = !detailsOpen">
                Detaljer

                <i class="fas fa-angle-down" aria-hidden="true" />
            </h4>

            <div class="columns is-tablet">
                <form-field
                    class="column"
                    :icon-right="true"
                    label="Service rabatsats"
                    help-text="Lad feltet være tomt som standard"
                    :error="error.service_discount"
                >
                    <span class="icon is-small is-right">
                        <i class="fas fa-percentage" />
                    </span>

                    <input-decimal
                        v-model="form.service_discount"
                        :error="error.service_discount"
                    />
                </form-field>

                <form-field
                    class="column"
                    :icon-right="true"
                    label="Vare rabatsats"
                    help-text="Lad feltet være tomt som standard"
                    :error="error.product_discount"
                >
                    <span class="icon is-small is-right">
                        <i class="fas fa-percentage" />
                    </span>

                    <input-decimal
                        v-model="form.product_discount"
                        :error="error.product_discount"
                    />
                </form-field>

                <form-field
                    class="column"
                    :icon-right="true"
                    label="Varerabat - global aftale"
                    help-text="Lad feltet være tomt som standard"
                    :error="error.global_product_discount"
                >
                    <span class="icon is-small is-right">
                        <i class="fas fa-percentage" />
                    </span>

                    <input-decimal
                        v-model="form.global_product_discount"
                        :error="error.global_product_discount"
                    />
                </form-field>
            </div>

            <div class="columns is-tablet">
                <form-field
                    class="column"
                    label="Vis priser på ydelser til kunden"
                    help-text="Lad feltet være tomt som standard"
                    :error="error.display_prices"
                >
                    <input-checkbox
                        v-model="form.display_prices"
                        :error="error.display_prices"
                    />
                </form-field>
                <form-field
                    class="column"
                    label="Spærret?"
                    help-text="Lad feltet være tomt som standard"
                    :error="error.blocked"
                >
                    <input-checkbox
                        v-model="form.blocked"
                        :error="error.blocked"
                    />
                </form-field>
                <div class="column">
                    <div class="div">
                        <form-field

                            inner-container-class="select is-fullwidth"
                            label="Standard skabelon"
                            help-text="Vælg Danmark for DK, og Udland for øvrige lande"
                            :error="error.default_template"
                        >
                            <input-select
                                v-model="form.default_template"
                                :error="error.default_template"
                                class="control"
                                :options="templates"
                                @input="setDefaultTemplateOptions"
                            />
                        </form-field>
                        <form-field

                            v-for="(
                                option, optionKey
                            ) in form.default_template_options"
                            :key="optionKey"
                        >
                            <input-checkbox
                                :label="option.label"
                                :true-value="true"
                                :false-value="false"
                                inline
                                v-model="option.value"
                            />
                        </form-field>
                    </div>
                </div>
            </div>
        </div>

        <template v-slot:footer class="has-space-between">
            <div class="footer-spacer">
                <div>
                    <button
                        class="button is-danger is-flex-left"
                        @click="reset"
                    >
                        Nulstil
                    </button>
                    <button
                        class="button is-default"
                        @click="$emit('closed', true)"
                    >
                        Annuller
                    </button>
                    <button class="button is-primary" @click="submit">
                        {{ saveButtonText }}
                    </button>
                </div>

                <button class="button is-danger" @click="remove" v-if="isEdit">
                    Slet
                </button>
            </div>
        </template>
    </modal>
</template>

<script>
import Modal from "@elems/modal";
import {
    FormField,
    InputText,
    InputCheckbox,
    InputSelect,
    InputDecimal,
    InputSelectSearch,
} from "@elems/form";
import cloneDeep from "lodash/cloneDeep";
import { mapGetters } from "vuex";

export default {
    components: {
        Modal,
        InputSelectSearch,
        FormField,
        InputText,
        InputSelect,
        InputCheckbox,
        InputDecimal,
    },
    props: {
        active: Boolean,
        customer: Object,
    },
    data() {
        return {
            detailsOpen: false,
            form: {
                name: "",
                address: "",
                zipcode: "",
                city: "",
                country: "DK",
                email: "",
                ean_number: "",
                cc_emails: "",
                cvr_number: "",
                phonenumber: "",
                kilometers: 0,
                customer_group: "",
                payment_critea: "",
                economic_id: "",
                blocked: 0,
                display_prices: 0,
                tax_zone: 0,
                currency: "DKK",
                service_discount: 0,
                product_discount: 0,
                global_product_discount: 0,
                default_template: 0,
                default_template_options: [],
            },
            error: [],
            economic: {
                vatZones: [],
                paymentTerms: [],
                customerGroups: [],
            },
        };
    },
    computed: {
        ...mapGetters(["listCountries", "findCountry"]),
        countryId: {
            get() {
                return this.form.country;
            },
            set(value) {
                this.form.country = value ? value.code : null;
            },
        },
        templates() {
            return window.app.templateType.filter(function (template) {
                return template.active === true;
            });
        },
        isEdit() {
            return this.customer != null;
        },
        saveButtonText() {
            return this.isEdit ? "Gem" : "Opret";
        },
        economicIsEnabled() {
            const setting =
                this.$store.getters.settingByKey(
                    "integration.economic.enabled"
                ) || {};
            return setting.value === 1;
        },
        isDisplayPricesEnabledByDefault() {
            const setting =
                this.$store.getters.settingByKey(
                    "company.prices.default_display_service"
                ) || {};
            return setting.value;
        },
        economicDefaults() {
            let settings = {};
            const keys = [
                "default_customer_group",
                "default_payment_term",
                "default_vat_zone",
            ];
            keys.forEach((key) => {
                const setting = this.$store.getters.settingByKey(
                    "integration.economic." + key
                );
                settings[key] = setting ? setting.value : 0;
            });
            return settings;
        },
        currencies() {
            return [
                { value: "DKK", title: "DKK" },
                { value: "EUR", title: "EUR" },
            ];
        },
    },
    methods: {
        findTemplate(templateId) {
            return this.templates.find(
                (template) => template.value == templateId
            );
        },
        getDefaultTemplateOptions(defaultTemplateId) {
            const template = this.findTemplate(defaultTemplateId);

            if (!template) {
                return [];
            }

            if (
                this.isEdit &&
                this.customer &&
                this.customer.default_template === defaultTemplateId
            ) {
                return cloneDeep({
                    ...template.options,
                    ...this.customer.default_template_options,
                });
            }

            return cloneDeep(template.options);
        },
        setDefaultTemplateOptions(defaultTemplateId) {
            this.form.default_template_options =
                this.getDefaultTemplateOptions(defaultTemplateId);
        },
        title() {
            return this.isEdit ? "Rediger kunde" : "Opret kunde";
        },
        submit() {
            return this.isEdit ? this.update() : this.create();
        },
        create() {
            if (!this.form.economic_id) {
                let economicId = parseInt(this.form.phonenumber).toString();
                const charCount =
                    ["dk", "denmark", "danmark"].indexOf(
                        this.form.country.toLowerCase()
                    ) > -1
                        ? 8
                        : 9;
                if (economicId.length < charCount) {
                    economicId = economicId.padEnd(charCount, "0");
                }
                this.form.economic_id = economicId.substring(
                    economicId.length - charCount
                );
            }
            window.axios
                .post("/api/customers", this.form)
                .then(({ data }) => {
                    this.$emit("updated");
                    this.$emit("closed");
                    this.$emit("created", data);
                    this.$store.dispatch("setSuccess", "Kunden blev oprettet");
                    this.reset();
                })
                .catch((error) => {
                    if (error.response && error.response.status === 422) {
                        this.error = error.response.data.errors;
                        this.detailsOpen = true;
                    }
                });
        },
        update() {
            window.axios
                .put("/api/customers/" + this.customer.id, this.form)
                .then(({ data }) => {
                    this.$emit("updated");
                    this.$emit("closed");
                    this.$store.dispatch("setSuccess", "Kunden blev opdateret");
                    this.reset();
                })
                .catch((error) => {
                    if (error.response && error.response.status === 422) {
                        this.error = error.response.data.errors;
                        this.detailsOpen = true;
                    }
                });
        },
        reset() {
            if (this.isEdit) {
                const customer = this.customer;

                this.form = {
                    name: customer.name,
                    address: customer.address,
                    zipcode: customer.zipcode,
                    city: customer.city,
                    country: customer.country,
                    email: customer.email,
                    ean_number: customer.ean_number,
                    cc_emails: customer.cc_emails,
                    cvr_number: customer.cvr_number,
                    phonenumber: customer.phonenumber,
                    kilometers: customer.kilometers,
                    customer_group: customer.customer_group,
                    payment_critea: customer.payment_critea,
                    blocked: customer.blocked ? 1 : 0,
                    display_prices: customer.display_prices ? 1 : 0,
                    tax_zone: customer.tax_zone,
                    currency: customer.currency,
                    service_discount: customer.service_discount,
                    product_discount: customer.product_discount,
                    global_product_discount: customer.global_product_discount,
                    default_template: customer.default_template,
                    default_template_options: this.getDefaultTemplateOptions(
                        customer.default_template
                    ),
                    economic_id: customer.economic_id,
                };
            } else {
                const defaultTemplate = this.templates.find(
                    (t) => t.is_default
                );
                const defaultTemplateId = defaultTemplate
                    ? defaultTemplate.value
                    : -1;

                this.form = {
                    name: "",
                    address: "",
                    zipcode: "",
                    city: "",
                    country: "DK",
                    email: "",
                    ean_number: "",
                    cc_emails: "",
                    cvr_number: "",
                    phonenumber: "",
                    kilometers: 0,
                    customer_group:
                        this.economicDefaults.default_customer_group,
                    payment_critea: this.economicDefaults.default_payment_term,
                    blocked: 0,
                    display_prices: this.isDisplayPricesEnabledByDefault
                        ? 1
                        : 0,
                    tax_zone: this.economicDefaults.default_vat_zone,
                    currency: "DKK",
                    service_discount: 0,
                    product_discount: 0,
                    global_product_discount: 0,
                    default_template: defaultTemplateId,
                    default_template_options:
                        this.getDefaultTemplateOptions(defaultTemplateId),
                    economic_id: "",
                };
            }
            this.error = [];
        },
        remove() {
            if (
                !confirm(
                    "Er du sikker på du vil slette " + this.form.name + "?"
                )
            ) {
                return;
            }

            window.axios
                .delete("/api/customers/" + this.customer.id)
                .then(() => {
                    this.$emit("updated");
                    this.$emit("closed");
                    this.$store.dispatch("setSuccess", "Kunden blev slettet");
                })
                .catch((error) => {
                    this.$store.dispatch(
                        "setError",
                        "Kunden kunne ikke slettes"
                    );
                });
        },
        async loadEconomicData() {
            const [custoemrGroups, vatZones, paymentTerms] = await Promise.all([
                axios.get("/api/integrations/economic/customer-groups"),
                axios.get("/api/integrations/economic/vat-zones"),
                axios.get("/api/integrations/economic/payment-terms"),
            ]);

            this.economic.customerGroups = custoemrGroups.data;
            this.economic.vatZones = vatZones.data;
            this.economic.paymentTerms = paymentTerms.data;
        },
        async loadSettings() {
            await this.$store.dispatch("loadSettings");
            this.reset();
        },
    },
    created() {
        this.$store.dispatch("loadCountries");

        this.loadSettings();
        this.loadEconomicData();
    },
    watch: {
        customer() {
            this.reset();
        },
        countryId(newValue) {
            const templateName = newValue.toLowerCase() == 'dk' ? 'Danmark' : 'Udland';
            const dkTemplate = this.templates.find(x => x.title.toLowerCase() == templateName.toLowerCase());

            if (!dkTemplate) {
                return;
            }
            this.form.default_template = dkTemplate.value;
            this.form.default_template_options = this.getDefaultTemplateOptions(dkTemplate.value);
        }
    },
};
</script>

<style lang="scss" scoped>
.footer-spacer {
    width: 100%;
    display: flex;
    flex-direction: row-reverse;
    justify-content: space-between;
}

.customer-details {
    h4 {
        display: flex;
        justify-content: space-between;
        align-items: center;
        border-bottom: 1px solid #bbb;
        margin-bottom: 25px;
        cursor: pointer;

        i {
            transition: transform 0.3s ease-in-out;
        }
    }

    .column {
        display: none;
    }

    &.open {
        h4 i {
            transform: rotate(0.5turn);
        }

        .column {
            display: block;
        }
    }
}

.column {
    padding: 0 0.75rem;
}
</style>
